<template>
  <div class="flex items-center space-x-2">
    <button
      type="button"
      class="flex items-center justify-center disabled:opacity-20"
      :disabled="isDecrementDisabled"
      @click="decrementDates"
    >
      <IconChevronLeft />
    </button>
    <button
      type="button"
      class="flex w-52 items-center justify-between rounded-full bg-background p-1 text-center"
      @click="showDialog"
    >
      <span
        class="mx-2 line-clamp-1 flex-1 text-center text-xs font-medium"
      >
        {{ timePeriodLabel }}
      </span>
      <IconChevronDown
        class="rounded-full bg-background-fg/10"
      />
    </button>
    <button
      type="button"
      class="flex items-center justify-center disabled:opacity-20"
      :disabled="isIncrementDisabled"
      @click="() => incrementDates()"
    >
      <IconChevronRight />
    </button>
  </div>
  <BaseDialog :is-open="isOpen" @close="onClose">
    <div class="p-2">
      <DateRangeSelect
        :model-value="modelValue"
        :type="rangeType"
        @update:model-value="onUpdateRangeSelect"
      />
      <div
        class="grid grid-cols-1 gap-2 p-2 sm:grid-cols-2"
      >
        <template
          v-for="quickAction in quickActions"
          :key="quickAction.label"
        >
          <button
            type="button"
            class="flex items-center justify-start space-x-2 rounded-lg border border-primary px-2 py-1"
            @click="quickAction.action"
          >
            <span>
              {{ quickAction.label }}
            </span>
          </button>
        </template>
      </div>
    </div>
  </BaseDialog>
</template>

<script
  setup
  lang="ts"
  generic="TRangeType extends 'month' | 'custom'"
>
import * as DateFns from "date-fns"
import { type DateRangeSelectModelValue } from "./DateRangeSelect.vue"

const props = defineProps<{
  modelValue: DateRangeSelectModelValue<TRangeType>
  timePeriodLabel: string
  rangeType: TRangeType
}>()

const emit = defineEmits<{
  (
    event: "update:modelValue",
    params: DateRangeSelectModelValue<TRangeType>
  ): void
}>()

const modelValueAsDate = computed(() => ({
  start: props.modelValue.start
    ? DateFns.parse(
        props.modelValue.start,
        "yyyy-MM-dd",
        new Date()
      )
    : undefined,
  end: props.modelValue.end
    ? DateFns.parse(
        props.modelValue.end,
        "yyyy-MM-dd",
        new Date()
      )
    : undefined,
}))

const isOpen = ref(false)
const showDialog = () => {
  isOpen.value = true
}
const hideDialog = () => {
  isOpen.value = false
}

const quickActions = computed(() => {
  if (props.rangeType === "month") {
    return [
      {
        label: "This Month",
        action: () => {
          emit("update:modelValue", {
            start: formatToModelValue(
              DateFns.startOfMonth(new Date())
            ),
            end: formatToModelValue(
              DateFns.endOfMonth(new Date())
            ),
          })

          hideDialog()
        },
      },
      {
        label: "Last Month",
        action: () => {
          const lastMonth = DateFns.addMonths(
            new Date(),
            -1
          )
          emit("update:modelValue", {
            start: formatToModelValue(
              DateFns.startOfMonth(lastMonth)
            ),
            end: formatToModelValue(
              DateFns.endOfMonth(lastMonth)
            ),
          })
          hideDialog()
        },
      },
      {
        label: "This Year",
        action: () => {
          emit("update:modelValue", {
            start: formatToModelValue(
              DateFns.startOfYear(new Date())
            ),
            end: formatToModelValue(
              DateFns.endOfYear(new Date())
            ),
          })
          hideDialog()
        },
      },
    ]
  }

  return [
    {
      label: "Today",
      action: () => {
        emit("update:modelValue", {
          start: formatToModelValue(new Date()),
          end: formatToModelValue(new Date()),
        })
        hideDialog()
      },
    },
    {
      label: "This Week",
      action: () => {
        emit("update:modelValue", {
          start: formatToModelValue(
            DateFns.startOfISOWeek(new Date())
          ),
          end: formatToModelValue(
            DateFns.endOfISOWeek(new Date())
          ),
        })
        hideDialog()
      },
    },
    {
      label: "This Month",
      action: () => {
        emit("update:modelValue", {
          start: formatToModelValue(
            DateFns.startOfMonth(new Date())
          ),
          end: formatToModelValue(
            DateFns.endOfMonth(new Date())
          ),
        })
        hideDialog()
      },
    },
    {
      label: "This Year",
      action: () => {
        emit("update:modelValue", {
          start: formatToModelValue(
            DateFns.startOfYear(new Date())
          ),
          end: formatToModelValue(
            DateFns.endOfYear(new Date())
          ),
        })
        hideDialog()
      },
    },
    {
      label: "All Time",
      action: () => {
        emit("update:modelValue", {
          start: undefined,
          end: undefined,
        } as DateRangeSelectModelValue<TRangeType>)
        hideDialog()
      },
    },
  ]
})

const onClose = () => {
  hideDialog()
}

const onUpdateRangeSelect = (
  value: DateRangeSelectModelValue<TRangeType>
) => {
  emit("update:modelValue", value)
  hideDialog()
}

const incrementDates = (step = 1) => {
  const next = getNewDates(step)
  if (!next) return

  emit("update:modelValue", {
    start: next.start
      ? formatToModelValue(next.start)
      : undefined,
    end: next.end
      ? formatToModelValue(next.end)
      : undefined,
  } as DateRangeSelectModelValue<TRangeType>)
}

const formatToModelValue = (date: Date) =>
  DateFns.format(date, "yyyy-MM-dd")

const decrementDates = () => incrementDates(-1)

const getNewDates = (
  step: number
): { start?: Date; end?: Date } | undefined => {
  const { start, end } = modelValueAsDate.value

  if (!start && !end) return undefined
  if (start && !end)
    return { start: DateFns.addDays(start, step) }
  if (!start && end)
    return { end: DateFns.addDays(end, step) }

  if (start && end) {
    if (isRangeInFullYears(start, end)) {
      return {
        start: DateFns.addYears(start, step),
        end: DateFns.endOfYear(DateFns.addYears(end, step)),
      }
    }

    if (isRangeInFullMonths(start, end)) {
      return {
        start: DateFns.addMonths(start, step),
        end: DateFns.endOfMonth(
          DateFns.addMonths(end, step)
        ),
      }
    }

    const diffDays =
      DateFns.differenceInDays(end, start) + 1
    return {
      start: DateFns.addDays(start, diffDays * step),
      end: DateFns.addDays(end, diffDays * step),
    }
  }
}

const isIncrementDisabled = computed(() => {
  const { start, end } = modelValueAsDate.value
  if (!start && !end) return true
  if (!start && end) return false

  const newStartDate = getNewDates(1)?.start

  return (
    !newStartDate ||
    DateFns.isAfter(newStartDate, DateFns.endOfToday())
  )
})

const isDecrementDisabled = computed(() => {
  const { start, end } = modelValueAsDate.value

  return !start && !end
})
</script>
